import React from 'react';
import { Layers, ShieldCheck, Workflow, CheckCircle, GraduationCap, Sparkles, Clock } from 'lucide-react';

const Skills = () => {
  const expertiseGroups = [
    {
      title: "Product, Delivery & Transformation",
      icon: <Layers className="text-brand-primary" size={32} />,
      skills: [
        "Product and project delivery",
        "Digital transformation",
        "Enterprise systems implementation",
        "Product discovery and requirements definition",
        "Technical requirements and process mapping",
        "UAT and implementation readiness",
        "Stakeholder management and cross-functional delivery",
        "Change management and user adoption"
      ]
    },
    {
      title: "Data, Governance & Security",
      icon: <ShieldCheck className="text-brand-primary" size={32} />,
      skills: [
        "Data migration and data quality",
        "Data governance operations",
        "GDPR and privacy operations",
        "ISO 27001-aligned governance",
        "Risk, controls and auditability",
        "Secure-by-design process improvement",
        "Information security and operational resilience"
      ]
    },
    {
      title: "Automation & Digital Platforms",
      icon: <Workflow className="text-brand-primary" size={32} />,
      skills: [
        "Microsoft 365",
        "Power Automate",
        "Workflow automation",
        "SharePoint and document/process automation",
        "JSON and API/integration literacy",
        "AI-assisted digital product creation",
        "AI agents with Copilot Studio",
        "Internal solutions development"
      ]
    }
  ];

  const professionalDevelopment = [
    {
      title: "Advanced Professional Certificate in Leadership and Management",
      issuer: "London School of Business Administration | CPD-accredited",
      detail: "Focus: strategic leadership, corporate governance, strategic risk and resilience, organisational change, communication, emotional intelligence and team leadership.",
      status: "In progress",
      icon: <Clock className="text-brand-primary" size={20} />
    },
    {
      title: "ISO/IEC 27001:2022 Lead Implementer",
      issuer: "Advisera",
      detail: "Focus: Information Security Management Systems, risk management, controls implementation, governance and continuous improvement.",
      status: null,
      icon: <GraduationCap className="text-brand-primary" size={20} />
    },
    {
      title: "Generative AI Mastermind",
      issuer: "Outskill",
      detail: "Focus: applied generative AI — prompting, AI-assisted product building, agents and practical automation.",
      status: null,
      icon: <Sparkles className="text-brand-primary" size={20} />
    }
  ];

  const certifications = [
    { title: "OneTrust Data Mapping Expert", issuer: "OneTrust" },
    { title: "OneTrust Certified Privacy Professional", issuer: "OneTrust" },
    { title: "Google Project Management", issuer: "Google via Coursera" },
    { title: "Business English", issuer: "British Council" },
    { title: "Business Skills Development Certificate", issuer: "American Chamber of Commerce" }
  ];

  return (
    <section id="skills" className="py-24 bg-bg-card" data-testid="skills-section">
      <div className="container mx-auto px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-2 text-brand-primary mb-6">Core Expertise</h2>
            <p className="body-large max-w-3xl mx-auto">
              Delivery, governance and automation capability spanning product operations, data quality, information security and enterprise change
            </p>
          </div>

          {/* Expertise Groups */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {expertiseGroups.map((group, index) => (
              <div key={index} className="team-card p-8" data-testid={`expertise-group-${index}`}>
                <div className="flex items-start space-x-4 mb-6">
                  {group.icon}
                  <h3 className="heading-5 text-text-primary">{group.title}</h3>
                </div>
                <ul className="space-y-3">
                  {group.skills.map((skill, skillIndex) => (
                    <li key={skillIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-2"></div>
                      <span className="body-small text-text-primary">{skill}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Professional Development */}
          <div className="mb-16">
            <h3 className="heading-4 text-text-primary mb-8 text-center">Professional Development</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {professionalDevelopment.map((item, index) => (
                <div key={index} className="team-card p-6" data-testid={`prof-dev-${index}`}>
                  <div className="flex items-start space-x-3 mb-3">
                    {item.icon}
                    <div>
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h4 className="body-medium text-text-primary">{item.title}</h4>
                        {item.status && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-brand-primary/10 text-brand-primary border border-brand-primary/30">
                            {item.status}
                          </span>
                        )}
                      </div>
                      <p className="caption text-text-secondary mb-2">{item.issuer}</p>
                      <p className="body-small text-text-secondary">{item.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Other certifications */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map((cert, index) => (
                <div key={index} className="team-card p-6" data-testid={`cert-${index}`}>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="body-medium text-text-primary mb-1">{cert.title}</h4>
                      <p className="caption text-text-secondary">{cert.issuer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
